import { useAuth } from "@contexts/auth";
import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Avatar,
  Button,
  Checkbox,
  Group,
  Modal,
  Radio,
  ScrollAreaAutosize,
  Select,
  Stack,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconFile,IconPencil, IconTrash, IconUpload } from "@tabler/icons-react";
import i18n from "src/i18n";
import { z } from "zod";

import { MultiSelectWithSearch } from "@components/MultiSelectWithSearch";
import { SpecialtyMultiInput } from "@components/SpecialtyMultiInput";

import { showErrorNotification } from "@utils/showNotification";

import { COUNTRY_CODES } from "@constants/countryCodes";
import { imageFileTypes } from "@constants/fileTypes";

import { useCreateProfile } from "@hooks/mutations/profile/useCreateProfile";
import { useGetClinicConsultationServices } from "@hooks/queries/clinic/useGetClinicConsultationServices";
import { useGetProfiles } from "@hooks/queries/profile/useGetProfiles";
import { usePrimarySpecializations, primarySpecializationOptions } from "@hooks/queries/profile/usePrimarySpecializations";
import { useGetDoctorsForConsultationService } from "@hooks/queries/user/useGetDoctorsForConsultationService";

import { StyledAddDoctorModalContent } from "./AddDoctorModal.styled";


import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";


const doctorFormSchema = () =>
  z.object({
    doctor: z.string().min(1, i18n.t("doctor-required")),
    partnerId: z.string().optional(),
    primarySpecializationId: z.string().min(1, "Select a primary specialization"),
    specialties: z.array(z.object({
      label: z.string(),
      value: z.string(),
    })).optional(),
    visibility: z.enum(["public", "private"]),
    services: z.array(z.object({
      label: z.string(),
      value: z.string(),
    })).optional(),
    homecare: z.boolean(),
    teleconsultation: z.boolean(),
    languages: z.array(z.string()).optional(),
    location: z.string().optional(),
    licenseNumber: z.string().optional(),
    licensedCountries: z.array(z.object({
      label: z.string(),
      value: z.string(),
    })).optional(),
    yearsOfExperience: z.string().optional(),
    bio: z.string().optional(),
    professionalMembership: z.array(z.string()).optional(),
    awardsPublications: z.array(z.string()).optional(),
  });

type DoctorFormInputs = z.infer<ReturnType<typeof doctorFormSchema>>;

interface AddDoctorModalProps {
  opened: boolean;
  close: () => void;
}

// File upload state interface (separate from form data)
interface FileUploads {
  photo?: File;
  signature?: File;
  license?: File;
}

export const AddDoctorModal = ({ opened, close }: AddDoctorModalProps) => {
  const { t } = useTranslation();
  const { can } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);

  // File uploads state (separate from form data)
  const [fileUploads, setFileUploads] = useState<FileUploads>({
    photo: undefined,
    signature: undefined,
    license: undefined,
  });

  // Mantine form with zodResolver
  const form = useForm<DoctorFormInputs>({
    mode: "uncontrolled",
    validate: zodResolver(doctorFormSchema()),
    initialValues: {
      doctor: "",
      partnerId: "",
      primarySpecializationId: "",
      specialties: [],
      visibility: can("profiles.publish") ? "public" : "private",
      services: [],
      homecare: false,
      teleconsultation: false,
      languages: [],
      location: "",
      licenseNumber: "",
      licensedCountries: [],
      yearsOfExperience: "",
      bio: "",
      professionalMembership: [],
      awardsPublications: [],
    },
  });

  const { data: partnersData } = useGetPartners({ isActive: true, isEnabled: can("access.manage") && opened });
  const { data: primarySpecializations } = usePrimarySpecializations();

  const partnersOptions = useMemo(() => {
    return (partnersData || []).map(partner => ({
      label: partner.name,
      value: partner._id,
    }));
  }, [partnersData]);

  // Fetch all doctors
  const { data: allDoctorsData } = useGetDoctorsForConsultationService({
    role: "doctor", accountOptions: true,
    isEnabled: opened,
  });

  // Fetch existing doctor profiles to filter out doctors who already have profiles
  const { data: existingProfilesData, isLoading: isLoadingProfiles } = useGetProfiles({
    type: "doctor",
    isEnabled: opened,
  });

  // Filter doctors without profiles
  const doctorsData = useMemo(() => {
    if (!allDoctorsData || !existingProfilesData) {
      console.log("Waiting for data...", { allDoctorsData: !!allDoctorsData, existingProfilesData: !!existingProfilesData });
      return allDoctorsData;
    }

    const existingUserIds = new Set(
      existingProfilesData.newData.map(profile => profile.userId)
    );

    const filteredDoctors = allDoctorsData.filter(doctor => !existingUserIds.has(doctor.value));

    console.log("Doctor filtering results:", {
      totalDoctors: allDoctorsData.length,
      existingProfiles: existingProfilesData.newData.length,
      availableDoctors: filteredDoctors.length,
      existingUserIds: Array.from(existingUserIds)
    });

    return filteredDoctors;
  }, [allDoctorsData, existingProfilesData]);

  // Fetch services data with names as values for API submission
  const { data: servicesData } = useGetClinicConsultationServices();

  // Profile creation mutation
  const { mutate: createProfile, isLoading: isCreatingProfile } = useCreateProfile();









  // Photo upload
  const onPhotoDropAccepted = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFileUploads(prev => ({ ...prev, photo: acceptedFiles[0] }));
    }
  }, []);

  const {
    getRootProps: getPhotoRootProps,
    getInputProps: getPhotoInputProps,
    isDragActive: isPhotoDragActive,
  } = useDropzone({
    accept: { "image/*": imageFileTypes },
    onDrop: onPhotoDropAccepted,
    multiple: false,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  // Signature upload
  const onSignatureDropAccepted = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFileUploads(prev => ({ ...prev, signature: acceptedFiles[0] }));
    }
  }, []);

  const {
    getRootProps: getSignatureRootProps,
    getInputProps: getSignatureInputProps,
    isDragActive: isSignatureDragActive,
  } = useDropzone({
    accept: { "image/*": imageFileTypes },
    onDrop: onSignatureDropAccepted,
    multiple: false,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  // License upload
  const onLicenseDropAccepted = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFileUploads(prev => ({ ...prev, license: acceptedFiles[0] }));
    }
  }, []);

  const {
    getRootProps: getLicenseRootProps,
    getInputProps: getLicenseInputProps,
    isDragActive: isLicenseDragActive,
  } = useDropzone({
    accept: { "image/*": imageFileTypes },
    onDrop: onLicenseDropAccepted,
    multiple: false,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Handle step navigation vs final submission
    if (currentStep < 4) {
      // Navigation: advance to next step
      if (validateCurrentStep()) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      // Final step: submit the form
      form.onSubmit(
        (values) => handleSave(values),
        (errors) => {
          const firstErrorPath = Object.keys(errors)[0];
          form.getInputNode(firstErrorPath)?.focus();
        }
      )(e);
    }
  };

  // Helper function to truncate file names
  const truncateFileName = (fileName: string, maxLength = 10): string => {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // No extension found
      return fileName.length > maxLength ? fileName.substring(0, maxLength) + '...' : fileName;
    }

    const nameWithoutExtension = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);

    if (nameWithoutExtension.length <= maxLength) {
      return fileName;
    }

    // Take first 5 characters and last 5 characters of the name (without extension)
    const firstPart = nameWithoutExtension.substring(0, 5);
    const lastPart = nameWithoutExtension.substring(nameWithoutExtension.length - 5);

    return `${firstPart}...${lastPart}${extension}`;
  };

  // Step validation helper
  const validateCurrentStep = () => {
    const formValues = form.getValues();
    switch (currentStep) {
      case 1:
        // Basic info step - only doctor is required
        if (!formValues.doctor) {
          showErrorNotification({
            title: "invalid-input-title",
            message: "doctor-required"
          });
          return false;
        }
        return true;
      case 2:
      case 3:
      case 4:
        // Professional details, bio, and files steps - no required fields
        return true;
      default:
        return true;
    }
  };



  const handleSave = async (formValues: DoctorFormInputs) => {
    setIsSaving(true);

    // Get selected doctor info
    const selectedDoctor = doctorsData?.find((doctor: any) => doctor.value === formValues.doctor);
    if (!selectedDoctor) {
      showErrorNotification({
        title: "oops",
        message: "doctor-not-found"
      });
      setIsSaving(false);
      return;
    }

    // Prepare consultation modes
    const consultationModes: string[] = [];
    if (formValues.teleconsultation) {
      consultationModes.push("teleconsultation");
    }
    if (formValues.homecare) {
      consultationModes.push("homecare");
    }

    // Build FormData for multipart/form-data request
    const profileData = new FormData();

    // Required fields
    profileData.append("profile_type", "doctor");
    profileData.append("user_id", formValues.doctor);
    profileData.append("visibility", can("profiles.publish") ? formValues.visibility : "private");
    if (can("access.manage") && formValues.partnerId) {
      profileData.append("partner_id", formValues.partnerId);
    }

    // Optional text fields (only send if not empty)
    profileData.append("primary_specialization_id", formValues.primarySpecializationId);
    if (formValues.bio) {
      profileData.append("brief_description", formValues.bio);
    }
    if (formValues.location) {
      profileData.append("location", formValues.location);
    }
    if (formValues.yearsOfExperience) {
      profileData.append("years_of_experience", formValues.yearsOfExperience);
    }
    if (formValues.licenseNumber) {
      profileData.append("license_number", formValues.licenseNumber);
    }

    // Array fields (only send if not empty)
    if (formValues.specialties && formValues.specialties.length > 0) {
      formValues.specialties.forEach(specialty => {
        profileData.append("specialties", specialty.label);
      });
    }
    if (formValues.services && formValues.services.length > 0) {
      formValues.services.forEach(service => {
        profileData.append("services", service.value);
      });
    }
    if (formValues.languages && formValues.languages.length > 0) {
      formValues.languages.forEach(language => {
        profileData.append("languages", language);
      });
    }
    if (formValues.licensedCountries && formValues.licensedCountries.length > 0) {
      formValues.licensedCountries.forEach(country => {
        profileData.append("licensed_countries", country.value);
      });
    }
    if (formValues.awardsPublications && formValues.awardsPublications.length > 0) {
      formValues.awardsPublications.forEach(award => {
        profileData.append("awards_and_publications", award);
      });
    }
    if (formValues.professionalMembership && formValues.professionalMembership.length > 0) {
      formValues.professionalMembership.forEach(membership => {
        profileData.append("professional_memberships", membership);
      });
    }
    if (consultationModes.length > 0) {
      consultationModes.forEach(mode => {
        profileData.append("consultation_modes", mode);
      });
    }

    // Files (only if provided)
    if (fileUploads.photo) {
      profileData.append("profile_image", fileUploads.photo);
    }
    if (fileUploads.signature) {
      profileData.append("signature", fileUploads.signature);
    }
    if (fileUploads.license) {
      profileData.append("license", fileUploads.license);
    }

    // First, create the profile
    createProfile(profileData, {
      onSuccess: () => {
        setIsSaving(false);
        close();
      },
      onError: (error) => {
        console.error("Profile creation failed:", error);
        setIsSaving(false);
      },
    });
  };

  const handleClose = () => {
    setCurrentStep(1);
    form.reset();
    setFileUploads({
      photo: undefined,
      signature: undefined,
      license: undefined,
    });
    close();
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return t("basic-info");
      case 2: return t("professional-details");
      case 3: return t("bio");
      case 4: return t("files");
      default: return "";
    }
  };

  return (
    <Modal
      centered
      size="md"
      title={<Text fw={500}>{t("add-profile")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
    >
      <StyledAddDoctorModalContent>
        <form onSubmit={handleFormSubmit}>
          <Text className="step-title">
            {getStepTitle()}
          </Text>

          {currentStep === 1 && (
            <Stack gap="md">
              {/* Doctor's Photo */}
              <div>
                <Text size="sm" fw={500} mb="xs">
                  {t("doctors-photo")}
                </Text>
                <Group justify="center" mb="md">
                  <div style={{ position: "relative" }}>
                    <Avatar size={80} radius="99px" src={fileUploads.photo ? URL.createObjectURL(fileUploads.photo) : null} />
                    <div
                      {...getPhotoRootProps()}
                      className="photo-upload-button"
                    >
                      <input {...getPhotoInputProps()} />
                      <IconPencil size={14} color="white" />
                    </div>
                  </div>
                </Group>
              </div>

            {/* Doctor */}
            <Select
              label={t("doctor")}
              placeholder={isLoadingProfiles ? t("loading") + "..." :
                          doctorsData?.length === 0 ? t("no-doctors-available") :
                          t("select-doctor")}
              data={doctorsData || []}
              {...form.getInputProps("doctor")}
              disabled={isLoadingProfiles || doctorsData?.length === 0}
            />

            {/* Partner */}
            <Select
              disabled={!can("access.manage")} label={t("partner")}
              placeholder={t("select-partner")}
              data={partnersOptions}
              searchable
              clearable
              {...form.getInputProps("partnerId")}
            />

            {/* Show helpful message when no doctors are available */}
            {!isLoadingProfiles && doctorsData?.length === 0 && (
              <Text size="sm" c="dimmed" mt="xs">
                {t("all-doctors-have-profiles-message", "All doctors already have profiles created.")}
              </Text>
            )}

            {/* Specialization */}
            <Select searchable
              label={t("specialization")}
              data={primarySpecializationOptions(primarySpecializations)}
              {...form.getInputProps("primarySpecializationId")}
              key={form.key("primarySpecializationId")}
            />

            {/* Specialties */}
            <SpecialtyMultiInput
              label={t("specialties")}
              placeholder={t("type-a-specialty")}
              value={form.getValues().specialties || []}
              onChange={(value) => form.setFieldValue("specialties", value)}
              error={form.errors.specialties as string}
            />

            {/* Visibility */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                {t("visibility")}
              </Text>
              <Radio.Group
                {...form.getInputProps("visibility")}
                key={form.key("visibility")}
              >
                <Stack gap={"0.1rem"}>
                  <Radio value="public" disabled={!can("profiles.publish")} label={t("public")} />
                  <Radio value="private" disabled={!can("profiles.publish")} label={t("private")} />
                </Stack>
              </Radio.Group>


            </div>
          </Stack>
        )}

        {currentStep === 2 && (
          <Stack gap="md">
            {/* Services */}
            <MultiSelectWithSearch
              label={t("services")}
              placeholder={t("enter-services")}
              data={servicesData || []}
              value={form.getValues().services || []}
              setValue={(value) => {
                if (typeof value === 'function') {
                  form.setFieldValue("services", value(form.getValues().services || []));
                } else {
                  form.setFieldValue("services", value);
                }
              }}
              maxDisplayedValues={999}
            />

            {/* Mode of Service */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                {t("mode-of-service")}
              </Text>
              <Stack gap="0.1rem">
                <Checkbox
                  label={t("homecare")}
                  {...form.getInputProps("homecare", { type: "checkbox" })}
                  key={form.key("homecare")}
                />
                <Checkbox
                  label={t("teleconsultation")}
                  {...form.getInputProps("teleconsultation", { type: "checkbox" })}
                  key={form.key("teleconsultation")}
                />
              </Stack>
            </div>

            {/* Languages */}
            <TagsInput
              label={t("languages")}
              placeholder={t("enter-language")}
              {...form.getInputProps("languages")}
              key={form.key("languages")}
              styles={{
                inputField: {
                  boxShadow: "none !important",
                },
              }}
            />

            {/* Location */}
            <TextInput
              label={t("location")}
              placeholder={t("location")}
              {...form.getInputProps("location")}
              key={form.key("location")}
            />

            {/* License Number */}
            <TextInput
              label={t("license-number")}
              placeholder={t("license-number")}
              {...form.getInputProps("licenseNumber")}
              key={form.key("licenseNumber")}
            />

            {/* Licensed Countries */}
            <MultiSelectWithSearch
              label={t("licensed-countries")}
              placeholder={t("select-countries")}
              data={COUNTRY_CODES}
              value={form.getValues().licensedCountries || []}
              setValue={(value) => {
                if (typeof value === 'function') {
                  form.setFieldValue("licensedCountries", value(form.getValues().licensedCountries || []));
                } else {
                  form.setFieldValue("licensedCountries", value);
                }
              }}
              maxDisplayedValues={999}
            />

            {/* Years of Experience */}
            <TextInput
              label={t("years-of-experience")}
              placeholder={t("years-of-experience")}
              {...form.getInputProps("yearsOfExperience")}
              key={form.key("yearsOfExperience")}
            />
          </Stack>
        )}

        {currentStep === 3 && (
          <Stack gap="md">
            {/* Bio */}
            <Textarea
              label={t("bio")}
              placeholder={t("enter-bio")}
              rows={4}
              {...form.getInputProps("bio")}
              key={form.key("bio")}
            />

            {/* Professional Membership */}
            <TagsInput
              label={t("professional-membership")}
              placeholder={t("type-a-membership")}
              {...form.getInputProps("professionalMembership")}
              key={form.key("professionalMembership")}
              styles={{
                inputField: {
                  boxShadow: "none !important",
                },
              }}
            />

            {/* Awards & Publications */}
            <TagsInput
              label={t("awards-publications")}
              placeholder={t("type-an-award")}
              {...form.getInputProps("awardsPublications")}
              key={form.key("awardsPublications")}
              styles={{
                inputField: {
                  boxShadow: "none !important",
                },
              }}
            />
          </Stack>
        )}

        {currentStep === 4 && (
          <Stack gap="lg">
            {/* Signature */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                {t("signature")}
              </Text>

              {fileUploads.signature ? (
                <div className="file-preview-container">
                  <div className="file-info">
                    <IconFile size={20} className="file-icon" />
                    <div className="file-details">
                      <div className="file-name">{truncateFileName(fileUploads.signature.name)}</div>
                      <div className="file-size">
                        {(fileUploads.signature.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                  <PermissionGate permission="profiles.archive"><ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    className="remove-button"
                    onClick={() => setFileUploads(prev => ({ ...prev, signature: undefined }))}
                  >
                    <IconTrash size={16} />
                  </ActionIcon></PermissionGate>
                </div>
              ) :
                <div className="file-upload-container" {...getSignatureRootProps()}>
                  <input {...getSignatureInputProps()} />
                  <Text size="sm" className="upload-text">
                    {t("browse-files-to-upload")}
                  </Text>
                  <Text size="sm" className="upload-info">
                    PNG, JPG, GIF up to 5MB
                  </Text>
                  <Button
                    variant="outline"
                    className="upload-button"
                    leftSection={<IconUpload size={16} className="upload-icon" />}
                  >
                    {t("browse-files")}
                  </Button>
                </div>
              }
            </div>

            {/* License */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                {t("license")}
              </Text>

              {fileUploads.license ? (
                <div className="file-preview-container">
                  <div className="file-info">
                    <IconFile size={20} className="file-icon" />
                    <div className="file-details">
                      <div className="file-name">{truncateFileName(fileUploads.license.name)}</div>
                      <div className="file-size">
                        {(fileUploads.license.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                  <PermissionGate permission="profiles.archive"><ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    className="remove-button"
                    onClick={() => setFileUploads(prev => ({ ...prev, license: undefined }))}
                  >
                    <IconTrash size={16} />
                  </ActionIcon></PermissionGate>
                </div>
              ) :
                <div className="file-upload-container" {...getLicenseRootProps()}>
                  <input {...getLicenseInputProps()} />
                  <Text size="sm" className="upload-text">
                    {t("browse-files-to-upload")}
                  </Text>
                  <Text size="sm" className="upload-info">
                    PNG, JPG, GIF up to 5MB
                  </Text>
                  <Button
                    variant="outline"
                    className="upload-button"
                    leftSection={<IconUpload size={16} className="upload-icon" />}
                  >
                    {t("browse-files")}
                  </Button>
                </div>
              }
            </div>
          </Stack>
        )}

          {/* Navigation Buttons */}
          <Group justify="flex-end" mt="xl">
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
              >
                {t("previous")}
              </Button>
            )}
            <PermissionGate permission="profiles.create"><Button
              type="submit"
              loading={isSaving || isCreatingProfile}
              disabled={currentStep === 4 && (!form.getValues().doctor || doctorsData?.length === 0)}
            >
              {currentStep < 4 ? t("next") : t("save")}
            </Button></PermissionGate>
          </Group>
        </form>
      </StyledAddDoctorModalContent>
    </Modal>
  );
};
